<template>
    <div id="manage-quick-reply">
        <h2 class="d-none d-sm-block">
            {{ $t("entities.quick-replies.Manage quick reply") }}
        </h2>
        <p class="d-none d-sm-block">
            {{ $t("entities.quick-replies.manage.description") }}
        </p>

        <perfect-scrollbar class="scroll-area">
            <validation-observer ref="simpleRules">
                <b-form>
                    <validation-provider name="channels" rules="required|min:1">
                        <b-form-group
                            slot-scope="{ valid, errors }"
                            :label="`${$t('entities.channels.Available channels')}*`"
                        >
                            <b-form-checkbox-group
                                id="channels-group"
                                v-model="quickReply.channels"
                                :state="errors[0] ? false : null"
                                name="channels"
                                stacked
                                text-field="name"
                                value-field="id"
                            >
                                <b-form-checkbox
                                    v-for="channel in channels"
                                    :key="channel.id"
                                    :value="{ id: channel.id }"
                                    class="w-100"
                                >
                                    <font-awesome-icon
                                        :icon="resolvePlatformIcon(channel.provider, channel.platform)"
                                    />
                                    {{ channel.name }}
                                </b-form-checkbox>
                            </b-form-checkbox-group>

                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <br />

                    <template v-if="manageAnyQuickReply">
                        {{ $t("entities.quick-replies.manage.visible-to") }}

                        <br />

                        <b-form-radio-group
                            id="btn-radios-privacy"
                            v-model="quickReply.private"
                            :options="privacyOptions"
                            button-variant="light"
                            buttons
                            name="radio-btn-outline"
                        />

                        <br /><br />
                    </template>

                    <strong>{{ $t("entities.quick-replies.quick-reply-message") }}</strong>

                    <validation-provider #default="{ errors }" :name="$t('Title')" rules="required">
                        <b-form-group :label="`${$t('Title')}*`" label-for="title">
                            <b-form-input id="title" v-model="quickReply.title" :placeholder="$t('Title')" />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider #default="{ errors }" :name="$t('Message')" rules="required">
                        <label for="content">{{ $t("Message") }}*</label>
                        <variable-picker :include="['default']" @select="insertVariable($event)" />

                        <div class="input-group">
                            <b-form-textarea
                                id="content"
                                ref="quickReplyTextArea"
                                v-model="quickReply.content"
                                :placeholder="$t('Message')"
                                maxlength="4096"
                                name="quickReplyContent"
                                rows="3"
                                @click="saveCursorPosition"
                                @keyup="saveCursorPosition"
                            />

                            <emoji-picker @select-emoji="selectEmoji($event, 'quickReplyContent')" />
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>

                    <br />

                    <b-row no-gutters>
                        <b-col cols="6">
                            <b-button
                                type="submit"
                                variant="light"
                                @click.prevent="$emit('close-manage-quick-reply-panel')"
                            >
                                {{ $t("Back") }}
                            </b-button>
                        </b-col>
                        <b-col cols="6">
                            <b-button
                                class="float-right"
                                type="submit"
                                variant="primary"
                                @click.prevent="validationForm"
                            >
                                {{ $t("entities.quick-replies.Save quick reply") }}
                            </b-button>
                        </b-col>
                    </b-row>
                </b-form>
            </validation-observer>
        </perfect-scrollbar>
    </div>
</template>

<script>
import { getUserData } from "@/auth/getUserData";
import VariablePicker from "@/components/variablePicker.vue";
import useChannels from "@/composables/v2.1/useChannels";
import Ability from "@/libs/acl/ability";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { resolvePlatformIcon } from "@/utils/channels";
import { addDataToInput } from "@/utils/input";
import { required } from "@validations";
import { ref } from "@vue/composition-api";
import {
    BButton,
    BCol,
    BForm,
    BFormCheckbox,
    BFormCheckboxGroup,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BFormTextarea,
    BListGroup,
    BListGroupItem,
    BPopover,
    BRow,
    VBTooltip,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import emojiPicker from "@/components/emojiPicker.vue";

export default {
    components: {
        BRow,
        BCol,
        BForm,
        BFormGroup,
        BButton,
        BFormCheckbox,
        BFormCheckboxGroup,
        BFormRadioGroup,
        BFormInput,
        BFormTextarea,
        BPopover,
        BListGroup,
        BListGroupItem,
        ValidationProvider,
        ValidationObserver,
        VariablePicker,
        emojiPicker,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        quickReplyId: {
            type: Number,
            required: false,
        },
    },
    data() {
        return {
            required,
            privacyOptions: [
                {
                    text: i18n.t("entities.quick-replies.manage.everybody"),
                    value: 0,
                },
                {
                    text: i18n.t("entities.quick-replies.manage.only-me"),
                    value: 1,
                },
            ],
        };
    },
    methods: {
        saveQuickReply(quickReplyData) {
            if (quickReplyData.id) {
                this.$emit("update-quick-reply", quickReplyData);
            } else {
                this.$emit("create-quick-reply", quickReplyData);
            }
        },
        validationForm() {
            this.$refs.simpleRules.validate().then((success) => {
                if (success) {
                    this.saveQuickReply(this.quickReply);
                }
            });
        },
    },
    setup(props) {
        const quickReply = ref({
            channels: [],
            userId: getUserData().id,
            content: "",
        });

        if (typeof props.quickReplyId !== "undefined") {
            axios
                .get(`/quick-replies/${props.quickReplyId}`, {
                    params: {
                        fields: "id,title,content,private",
                        join: "channels||id",
                    },
                })
                .then((result) => (quickReply.value = result.data));
        }

        let cursorPosition = 0;

        const saveCursorPosition = (event) => {
            if (event) {
                cursorPosition = event.target.selectionStart;
            }
        };

        const selectEmoji = (emoji, inputFieldName) => {
            quickReply.value.content = addDataToInput(inputFieldName, quickReply.value.content || "", emoji.data);
        };

        const quickReplyTextArea = ref();

        const insertVariable = (variableString) => {
            quickReplyTextArea.value.$el.focus();

            quickReply.value.content =
                quickReply.value.content.substr(0, cursorPosition) +
                " " +
                variableString +
                quickReply.value.content.substr(cursorPosition, quickReply.value.content.length);
        };

        const manageAnyQuickReply = Ability.rules
            .find((rule) => rule.subject === "quickReplies")
            .possession.includes("create:any");

        const { channels } = useChannels();
        return {
            quickReply,
            quickReplyTextArea,
            resolvePlatformIcon,
            saveCursorPosition,
            insertVariable,
            selectEmoji,
            manageAnyQuickReply,
            channels,
        };
    },
};
</script>
